import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('c2aa_token'));
  const [loading, setLoading] = useState(true);

  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Update axios headers when token changes
  useEffect(() => {
    if (token) {
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.Authorization;
    }
  }, [token, axiosInstance]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Login standard (admin/gestionnaire)
  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('c2aa_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  // Login chauffeur (email partagé + téléphone)
  const loginDriver = async (companyName, password, phone) => {
    const response = await axios.post(`${API_URL}/auth/driver/login`, { 
      company_name: companyName, 
      password,
      phone 
    });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('c2aa_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  // Register chauffeur
  const registerDriver = async (companyName, password, firstName, lastName, phone) => {
    const response = await axios.post(`${API_URL}/auth/driver/register`, {
      company_name: companyName,
      password,
      first_name: firstName,
      last_name: lastName,
      phone
    });
    return response.data;
  };

  // Register standard (pour admin)
  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('c2aa_token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'master_admin' || user?.role === 'super_admin' || user?.role === 'gestionnaire_c2';
  };

  const isMasterAdmin = () => {
    return user?.role === 'master_admin';
  };

  const isFleetManager = () => {
    return user?.role === 'admin_entreprise' || user?.role === 'gestionnaire_flotte';
  };

  const isDriver = () => {
    return user?.role === 'chauffeur';
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginDriver,
    registerDriver,
    register,
    logout,
    isAdmin,
    isMasterAdmin,
    isFleetManager,
    isDriver,
    axiosInstance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
